<template>
  <section class="px-3 pb-3">
    <AdminToastStack :toasts="toasts" @close="removeToast" />

    <div class="admin-surface">
      <div class="admin-surface__head">
        <span class="admin-surface__title">RSS-каналы</span>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadAll">
            <i class="bi bi-arrow-repeat me-1"></i>
            Обновить
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="resetChannelFilters">
            Сбросить фильтры
          </button>
          <button type="button" class="btn btn-primary btn-sm" :disabled="isLoading" @click="resetChannelForm">
            <i class="bi bi-plus-lg me-1"></i>
            Новый канал
          </button>
        </div>
      </div>

      <div class="row g-2 mt-1">
        <div class="col-12 col-lg-3">
          <select v-model="channelFilters.category" class="form-select form-select-sm">
            <option value="">Все категории</option>
            <option v-for="category in categoriesForFilter" :key="`filter-category-${category}`" :value="category">
              {{ category }}
            </option>
          </select>
        </div>
        <div class="col-6 col-lg-2">
          <select v-model="channelFilters.disabled" class="form-select form-select-sm">
            <option value="">Все</option>
            <option value="false">Активные</option>
            <option value="true">Отключенные</option>
          </select>
        </div>
        <div class="col-12 col-lg-5">
          <input
            v-model.trim="channelSearchQuery"
            type="search"
            class="form-control form-control-sm"
            placeholder="Поиск по id, названию, url, категории, клинеру"
          />
        </div>
        <div class="col-6 col-lg-2 d-grid">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="loadChannels">
            Применить
          </button>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-12 col-xxl-8">
          <div class="table-responsive admin-table-wrap">
            <table class="table table-sm align-middle mb-0 admin-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Категория</th>
                  <th>Статус</th>
                  <th>Клинер</th>
                  <th class="text-end">Действия</th>
                </tr>
              </thead>
              <tbody v-if="filteredChannels.length">
                <tr
                  v-for="channel in filteredChannels"
                  :key="channel.id"
                  :class="{ 'admin-table__row--active': channelForm.id === channel.id }"
                >
                  <td>
                    <div class="fw-semibold">{{ channel.name || '-' }}</div>
                    <div class="text-muted small">{{ channel.url || '-' }}</div>
                  </td>
                  <td>{{ channel.category || '-' }}</td>
                  <td>
                    <span :class="channel.disabled ? 'text-danger' : 'text-success'">
                      {{ channel.disabled ? 'Отключен' : 'Активен' }}
                    </span>
                  </td>
                  <td>
                    <span v-if="channel.cleaner">#{{ channel.cleaner.id }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td class="text-end">
                    <div class="btn-group btn-group-sm">
                      <button type="button" class="btn btn-outline-primary" @click="editChannel(channel)">Изменить</button>
                      <button type="button" class="btn btn-outline-danger" @click="openDeleteChannelModal(channel)">Удалить</button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="5" class="text-center text-muted py-3">
                    {{ isLoading ? 'Загрузка каналов...' : 'Каналы не найдены.' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-12 col-xxl-4">
          <div class="admin-detail">
            <div class="admin-detail__title">
              {{ channelForm.id ? `Редактирование канала #${channelForm.id}` : 'Создание канала' }}
            </div>
            <small class="text-muted">Обязательные: name, url, category</small>

            <form class="mt-2" @submit.prevent="submitChannel">
              <div class="row g-2">
                <div class="col-12">
                  <label class="form-label mb-1">Название *</label>
                  <input v-model.trim="channelForm.name" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">URL *</label>
                  <input v-model.trim="channelForm.url" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12 col-lg-6">
                  <label class="form-label mb-1">Категория *</label>
                  <select v-model="channelForm.category" class="form-select form-select-sm">
                    <option value="">Выберите категорию</option>
                    <option v-for="category in categoriesForChannelForm" :key="`form-category-${category}`" :value="category">
                      {{ category }}
                    </option>
                  </select>
                </div>
                <div class="col-12 col-lg-6 d-flex align-items-end">
                  <div class="form-check">
                    <input id="rss-channel-disabled" v-model="channelForm.disabled" type="checkbox" class="form-check-input" />
                    <label for="rss-channel-disabled" class="form-check-label">Отключен</label>
                  </div>
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Описание</label>
                  <textarea v-model="channelForm.description" rows="3" class="form-control form-control-sm"></textarea>
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Клинер</label>
                  <select v-model="channelForm.cleanerSelection" class="form-select form-select-sm">
                    <option v-if="channelForm.id" value="UNCHANGED">{{ selectedCleanerCaption }}</option>
                    <option v-if="channelForm.id" value="CLEAR">Отвязать клинер</option>
                    <option v-else value="">Без клинера</option>
                    <option v-for="cleaner in availableCleanersForChannel" :key="`cleaner-option-${cleaner.id}`" :value="String(cleaner.id)">
                      #{{ cleaner.id }} | {{ cleaner.textSelector || 'селектор' }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="resetChannelForm">
                  Очистить
                </button>
                <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">
                  {{ channelForm.id ? 'Сохранить' : 'Создать' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div class="admin-surface mt-3">
      <div class="admin-surface__head">
        <span class="admin-surface__title">RSS-клинеры</span>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadCleaners">
            <i class="bi bi-arrow-repeat me-1"></i>
            Обновить
          </button>
          <button type="button" class="btn btn-primary btn-sm" :disabled="isLoading" @click="resetCleanerForm">
            <i class="bi bi-plus-lg me-1"></i>
            Новый клинер
          </button>
        </div>
      </div>

      <div class="row g-2 mt-1">
        <div class="col-12 col-lg-6">
          <input
            v-model.trim="cleanerSearchQuery"
            type="search"
            class="form-control form-control-sm"
            placeholder="Поиск по id, селектору, тегам, словам, каналу"
          />
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-12 col-xxl-8">
          <div class="table-responsive admin-table-wrap">
            <table class="table table-sm align-middle mb-0 admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Селектор текста</th>
                  <th>Теги</th>
                  <th>Слова</th>
                  <th>Канал</th>
                  <th class="text-end">Действия</th>
                </tr>
              </thead>
              <tbody v-if="filteredCleaners.length">
                <tr
                  v-for="cleaner in filteredCleaners"
                  :key="cleaner.id"
                  :class="{ 'admin-table__row--active': cleanerForm.id === cleaner.id }"
                >
                  <td>{{ cleaner.id }}</td>
                  <td>{{ cleaner.textSelector || '-' }}</td>
                  <td>{{ cleaner.deleteTags || '-' }}</td>
                  <td>{{ cleaner.deleteWords || '-' }}</td>
                  <td>
                    <span v-if="cleaner.attachedChannelId">
                      #{{ cleaner.attachedChannelId }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td class="text-end">
                    <div class="btn-group btn-group-sm">
                      <button type="button" class="btn btn-outline-primary" @click="editCleaner(cleaner)">Изменить</button>
                      <button
                        type="button"
                        class="btn btn-outline-danger"
                        :disabled="Boolean(cleaner.attachedChannelId)"
                        :title="cleaner.attachedChannelId ? 'Клинер привязан к каналу' : 'Удалить клинер'"
                        @click="openDeleteCleanerModal(cleaner)"
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="6" class="text-center text-muted py-3">
                    {{ isLoading ? 'Загрузка клинеров...' : 'Клинеры не найдены.' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-12 col-xxl-4">
          <div class="admin-detail">
            <div class="admin-detail__title">
              {{ cleanerForm.id ? `Редактирование клинера #${cleanerForm.id}` : 'Создание клинера' }}
            </div>
            <small class="text-muted">Обязательное: textSelector</small>

            <form class="mt-2" @submit.prevent="submitCleaner">
              <div class="row g-2">
                <div class="col-12">
                  <label class="form-label mb-1">Селектор текста *</label>
                  <textarea v-model.trim="cleanerForm.textSelector" rows="2" class="form-control form-control-sm"></textarea>
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Удаляемые теги</label>
                  <textarea v-model.trim="cleanerForm.deleteTags" rows="2" class="form-control form-control-sm"></textarea>
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Удаляемые слова</label>
                  <textarea v-model.trim="cleanerForm.deleteWords" rows="2" class="form-control form-control-sm"></textarea>
                </div>
              </div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="resetCleanerForm">
                  Очистить
                </button>
                <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">
                  {{ cleanerForm.id ? 'Сохранить' : 'Создать' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDeleteConfirmModal" class="delete-content-backdrop" @click.self="closeDeleteConfirmModal">
      <div class="modal-container delete-content-modal">
        <div class="delete-content-head">
          <div class="delete-content-icon"><i class="bi bi-exclamation-triangle"></i></div>
          <div class="delete-content-title-wrap">
            <h6 class="delete-content-title mb-0">{{ deleteConfirmTitle }}</h6>
            <div class="delete-content-subtitle">Эта операция необратима</div>
          </div>
          <button type="button" class="delete-content-close" :disabled="deleteConfirmLoading" @click="closeDeleteConfirmModal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="delete-content-body">
          <div class="delete-content-item">
            <span class="text-muted">{{ deleteConfirmLabel }}</span>
            <strong>{{ deleteConfirmEntityLabel }}</strong>
          </div>
          <div v-if="deleteConfirmError" class="alert alert-danger py-1 px-2 small mt-2 mb-0">{{ deleteConfirmError }}</div>
        </div>

        <div class="delete-content-footer">
          <button class="btn btn-secondary btn-sm me-2" :disabled="deleteConfirmLoading" @click="closeDeleteConfirmModal">Отмена</button>
          <button class="btn btn-danger btn-sm" :disabled="deleteConfirmLoading" @click="confirmDeleteTarget">
            <span v-if="deleteConfirmLoading" class="spinner-border spinner-border-sm me-1"></span>
            Удалить
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import AdminToastStack from '@/admin/components/AdminToastStack.vue'
import { useAdminRequestState } from '@/admin/composables/useAdminRequestState'
import {
  validateAdminRssChannelUpsertRequest,
  validateAdminRssCleanerUpsertRequest,
} from '@/admin/types/adminContracts'
import {
  createAdminRssChannel,
  createAdminRssCleaner,
  deleteAdminRssChannel,
  deleteAdminRssCleaner,
  getAdminRssChannelById,
  getAdminRssChannels,
  getAdminRssCleanerById,
  getAdminRssCleaners,
  getRssChannelCategories,
  updateAdminRssChannel,
  updateAdminRssCleaner,
} from '@/admin/api/adminService'
import { normalizeSearchText } from '@/admin/utils/adminApiUtils'

const channels = ref([])
const cleaners = ref([])
const rssCategories = ref([])

const channelFilters = reactive({
  category: '',
  disabled: '',
})

const channelSearchQuery = ref('')
const cleanerSearchQuery = ref('')

const channelForm = reactive({
  id: null,
  name: '',
  url: '',
  description: '',
  category: '',
  disabled: false,
  cleanerSelection: '',
})

const cleanerForm = reactive({
  id: null,
  textSelector: '',
  deleteTags: '',
  deleteWords: '',
})

const showDeleteConfirmModal = ref(false)
const deleteConfirmType = ref('')
const deleteConfirmTarget = ref(null)
const deleteConfirmLoading = ref(false)
const deleteConfirmError = ref('')

const {
  isLoading,
  toasts,
  removeToast,
  clearMessages,
  pushError,
  execute,
} = useAdminRequestState()

const filteredChannels = computed(() => {
  const query = normalizeSearchText(channelSearchQuery.value)
  return channels.value.filter(channel => {
    if (!query) return true
    const cleanerText = channel.cleaner ? `${channel.cleaner.id} ${channel.cleaner.textSelector || ''}` : ''
    const haystack = `${channel.id} ${channel.name} ${channel.url} ${channel.category} ${channel.description || ''} ${cleanerText}`.toLowerCase()
    return haystack.includes(query)
  })
})

const categoriesForFilter = computed(() => {
  const set = new Set(rssCategories.value)
  const selected = String(channelFilters.category || '').trim()
  if (selected) set.add(selected)
  return [...set]
})

const categoriesForChannelForm = computed(() => {
  const set = new Set(rssCategories.value)
  const selected = String(channelForm.category || '').trim()
  if (selected) set.add(selected)
  return [...set]
})

const filteredCleaners = computed(() => {
  const query = normalizeSearchText(cleanerSearchQuery.value)
  return cleaners.value.filter(cleaner => {
    if (!query) return true
    const haystack =
      `${cleaner.id} ${cleaner.textSelector} ${cleaner.deleteTags || ''} ${cleaner.deleteWords || ''} ` +
      `${cleaner.attachedChannelId || ''} ${cleaner.attachedChannelName || ''}`.toLowerCase()
    return haystack.includes(query)
  })
})

const availableCleanersForChannel = computed(() =>
  cleaners.value.filter(cleaner => {
    if (!cleaner.attachedChannelId) return true
    return cleaner.attachedChannelId === channelForm.id
  })
)

const selectedCleanerCaption = computed(() => {
  const channel = channels.value.find(item => item.id === channelForm.id)
  if (!channel?.cleaner) return 'Оставить текущее: нет'
  return `Оставить текущее: #${channel.cleaner.id}`
})

const deleteConfirmTitle = computed(() =>
  deleteConfirmType.value === 'channel' ? 'Подтвердите удаление канала' : 'Подтвердите удаление клинера'
)

const deleteConfirmLabel = computed(() => (deleteConfirmType.value === 'channel' ? 'Канал:' : 'Клинер:'))

const deleteConfirmEntityLabel = computed(() => {
  const target = deleteConfirmTarget.value
  if (!target) return '-'
  if (deleteConfirmType.value === 'channel') {
    return target.name || target.url || `#${target.id || ''}`
  }
  return target.textSelector || `#${target.id || ''}`
})

const normalizeDisabledFilter = value => {
  if (value === 'true') return true
  if (value === 'false') return false
  return null
}

const parseCleanerSelection = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const openDeleteConfirmModal = (type, target) => {
  deleteConfirmType.value = String(type || '')
  deleteConfirmTarget.value = target || null
  deleteConfirmError.value = ''
  showDeleteConfirmModal.value = Boolean(target)
}

const closeDeleteConfirmModal = (force = false) => {
  if (deleteConfirmLoading.value && !force) return
  showDeleteConfirmModal.value = false
  deleteConfirmType.value = ''
  deleteConfirmTarget.value = null
  deleteConfirmError.value = ''
}

const openDeleteChannelModal = channel => {
  openDeleteConfirmModal('channel', channel)
}

const openDeleteCleanerModal = cleaner => {
  openDeleteConfirmModal('cleaner', cleaner)
}

const getAttachedChannelIdFromDeleteError = error => {
  const status = Number(error?.response?.status || 0)
  if (status !== 400) return null
  const message = String(error?.response?.data?.message || '')
  const match = message.match(/attached to channel id=(\d+)/i)
  if (!match) return null
  const channelId = Number(match[1])
  return Number.isFinite(channelId) ? channelId : null
}

const setChannelForm = channel => {
  channelForm.id = channel?.id || null
  channelForm.name = channel?.name || ''
  channelForm.url = channel?.url || ''
  channelForm.description = channel?.description || ''
  channelForm.category = channel?.category || rssCategories.value[0] || ''
  channelForm.disabled = Boolean(channel?.disabled)
  channelForm.cleanerSelection = channel?.id ? 'UNCHANGED' : ''
}

const resetChannelForm = () => {
  setChannelForm(null)
}

const setCleanerForm = cleaner => {
  cleanerForm.id = cleaner?.id || null
  cleanerForm.textSelector = cleaner?.textSelector || ''
  cleanerForm.deleteTags = cleaner?.deleteTags || ''
  cleanerForm.deleteWords = cleaner?.deleteWords || ''
}

const resetCleanerForm = () => {
  setCleanerForm(null)
}

const loadChannels = async () => {
  const list = await execute(
    () =>
      getAdminRssChannels({
        category: channelFilters.category,
        disabled: normalizeDisabledFilter(channelFilters.disabled),
      }),
    {
      error: 'Не удалось загрузить RSS-каналы.',
    }
  )
  channels.value = list

  if (channelForm.id) {
    const actual = list.find(item => item.id === channelForm.id)
    if (!actual) resetChannelForm()
  }
}

const loadCategories = async () => {
  const list = await execute(() => getRssChannelCategories(), {
    error: 'Не удалось загрузить категории RSS.',
  })
  rssCategories.value = Array.isArray(list) ? list : []
  if (!channelForm.id && !channelForm.category && rssCategories.value.length) {
    channelForm.category = rssCategories.value[0]
  }
}

const loadCleaners = async () => {
  const list = await execute(() => getAdminRssCleaners(), {
    error: 'Не удалось загрузить RSS-клинеры.',
  })
  cleaners.value = list

  if (cleanerForm.id && !list.some(item => item.id === cleanerForm.id)) {
    resetCleanerForm()
  }
}

const loadAll = async () => {
  clearMessages()
  try {
    await loadCategories()
  } catch {
    rssCategories.value = []
  }
  await loadCleaners()
  await loadChannels()
}

const resetChannelFilters = async () => {
  channelFilters.category = ''
  channelFilters.disabled = ''
  await loadChannels()
}

const editChannel = async channel => {
  clearMessages()
  const details = await execute(() => getAdminRssChannelById(channel.id), {
    error: 'Не удалось загрузить данные канала.',
  })
  setChannelForm(details)
}

const buildChannelPayload = () => ({
  name: channelForm.name,
  url: channelForm.url,
  description: channelForm.description,
  category: channelForm.category,
  disabled: channelForm.disabled,
})

const submitChannel = async () => {
  clearMessages()
  const payload = buildChannelPayload()
  const validationErrors = validateAdminRssChannelUpsertRequest(payload)
  if (validationErrors.length) {
    pushError(validationErrors[0])
    return
  }

  if (channelForm.id) {
    const updatePayload = { ...payload }
    if (channelForm.cleanerSelection === 'CLEAR') {
      updatePayload.clearCleaner = true
    } else if (channelForm.cleanerSelection !== 'UNCHANGED') {
      const cleanerId = parseCleanerSelection(channelForm.cleanerSelection)
      if (cleanerId != null) updatePayload.cleanerId = cleanerId
    }

    const updated = await execute(() => updateAdminRssChannel(channelForm.id, updatePayload), {
      success: 'Канал обновлен.',
      error: 'Не удалось обновить канал.',
    })
    setChannelForm(updated)
  } else {
    const createPayload = { ...payload }
    if (channelForm.cleanerSelection) {
      const cleanerId = parseCleanerSelection(channelForm.cleanerSelection)
      if (cleanerId != null) createPayload.cleanerId = cleanerId
    }

    const created = await execute(() => createAdminRssChannel(createPayload), {
      success: 'Канал создан.',
      error: 'Не удалось создать канал.',
    })
    setChannelForm(created)
  }

  await loadCleaners()
  await loadChannels()
}

const deleteChannelRow = async channel => {
  clearMessages()
  await execute(() => deleteAdminRssChannel(channel.id), {
    success: 'Канал удален.',
    error: 'Не удалось удалить канал.',
  })

  if (channelForm.id === channel.id) resetChannelForm()
  await loadCleaners()
  await loadChannels()
}

const editCleaner = async cleaner => {
  clearMessages()
  const details = await execute(() => getAdminRssCleanerById(cleaner.id), {
    error: 'Не удалось загрузить данные клинера.',
  })
  setCleanerForm(details)
}

const submitCleaner = async () => {
  clearMessages()
  const payload = {
    textSelector: cleanerForm.textSelector,
    deleteTags: cleanerForm.deleteTags,
    deleteWords: cleanerForm.deleteWords,
  }

  const validationErrors = validateAdminRssCleanerUpsertRequest(payload)
  if (validationErrors.length) {
    pushError(validationErrors[0])
    return
  }

  if (cleanerForm.id) {
    const updated = await execute(() => updateAdminRssCleaner(cleanerForm.id, payload), {
      success: 'Клинер обновлен.',
      error: 'Не удалось обновить клинер.',
    })
    setCleanerForm(updated)
  } else {
    const created = await execute(() => createAdminRssCleaner(payload), {
      success: 'Клинер создан.',
      error: 'Не удалось создать клинер.',
    })
    setCleanerForm(created)
  }

  await loadCleaners()
  await loadChannels()
}

const deleteCleanerRow = async cleaner => {
  clearMessages()
  try {
    await execute(() => deleteAdminRssCleaner(cleaner.id), {
      success: 'Клинер удален.',
      error: 'Не удалось удалить клинер.',
    })
  } catch (error) {
    const attachedChannelId = getAttachedChannelIdFromDeleteError(error)
    if (attachedChannelId != null) {
      channelFilters.category = ''
      channelFilters.disabled = ''
      await loadChannels()
      const attachedChannel = channels.value.find(item => item.id === attachedChannelId)
      if (attachedChannel) {
        setChannelForm(attachedChannel)
      } else {
        const details = await execute(() => getAdminRssChannelById(attachedChannelId), {
          error: `Клинер привязан к каналу #${attachedChannelId}, но не удалось загрузить данные канала.`,
        })
        setChannelForm(details)
      }
      return 'attached'
    }
    throw error
  }

  if (cleanerForm.id === cleaner.id) resetCleanerForm()
  await loadCleaners()
  await loadChannels()
  return 'deleted'
}

const confirmDeleteTarget = async () => {
  if (!deleteConfirmTarget.value) return

  deleteConfirmLoading.value = true
  deleteConfirmError.value = ''
  try {
    if (deleteConfirmType.value === 'channel') {
      await deleteChannelRow(deleteConfirmTarget.value)
      closeDeleteConfirmModal(true)
      return
    }

    if (deleteConfirmType.value === 'cleaner') {
      const status = await deleteCleanerRow(deleteConfirmTarget.value)
      if (status === 'deleted' || status === 'attached') {
        closeDeleteConfirmModal(true)
      }
      return
    }

    closeDeleteConfirmModal(true)
  } catch (error) {
    const message = String(error?.response?.data?.message || '').trim()
    deleteConfirmError.value = message || 'Не удалось выполнить удаление.'
  } finally {
    deleteConfirmLoading.value = false
  }
}

onMounted(async () => {
  await loadAll()
})
</script>

<style scoped>
.admin-surface {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.admin-surface__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-surface__title {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
}

.admin-table-wrap {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  max-height: 72vh;
  overflow: auto;
}

.admin-table thead th {
  position: sticky;
  top: 0;
  background: #f8fbff;
  z-index: 2;
  color: #607089;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e9edf6;
}

.admin-table__row--active td {
  background: #eef5ff;
}

.admin-detail {
  border: 1px solid #e4ebf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px;
}

.admin-detail__title {
  color: #1f2d45;
  font-weight: 700;
}

.delete-content-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
}

.delete-content-modal {
  background: #fff;
  border-radius: 14px;
  width: 92vw;
  max-width: 460px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.28);
  overflow: hidden;
  border: 1px solid #e5eaf6;
}

.delete-content-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px 10px;
  border-bottom: 1px solid #edf1f8;
}

.delete-content-icon {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: #fff3f3;
  color: #dc3545;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex: 0 0 34px;
}

.delete-content-title-wrap {
  min-width: 0;
}

.delete-content-title {
  color: #1f2d45;
  font-weight: 700;
}

.delete-content-subtitle {
  color: #6b7a93;
  font-size: 12px;
  margin-top: 2px;
}

.delete-content-close {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #7a879c;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.delete-content-close:hover {
  background: #f3f6fb;
  color: #2f3c54;
}

.delete-content-body {
  padding: 12px 14px;
}

.delete-content-item {
  border: 1px dashed #d8e1f2;
  border-radius: 10px;
  background: #f9fbff;
  padding: 8px 10px;
  font-size: 13px;
  color: #2a3752;
  display: flex;
  align-items: center;
  gap: 6px;
}

.delete-content-footer {
  padding: 10px 14px 14px;
  border-top: 1px solid #edf1f8;
  display: flex;
  justify-content: flex-end;
}
</style>
